import { Building2, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SignupAccountType } from "@/lib/signupMetadata";

interface SignupNameFieldsProps {
  accountType: SignupAccountType;
  /** A pre-activation invitation whose organization already exists. */
  isBoundInvite: boolean;
  fullName: string;
  organizationName: string;
  onFullNameChange: (value: string) => void;
  onOrganizationNameChange: (value: string) => void;
}

/**
 * The name inputs on the sign-up form. "Full Name" is always the PERSON and is
 * never prefilled. "Organization Name" appears only for an organization
 * account: editable for a legacy invitation (it seeds /admin/setup), read-only
 * for a bound one (the organization already exists). See lib/signupMetadata.ts
 * for what each value becomes.
 */
export function SignupNameFields({
  accountType,
  isBoundInvite,
  fullName,
  organizationName,
  onFullNameChange,
  onOrganizationNameChange,
}: SignupNameFieldsProps) {
  const isOrg = accountType === "organization";
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="fullName" className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          Full name
        </Label>
        <Input
          id="fullName"
          type="text"
          placeholder="John Smith"
          value={fullName}
          onChange={(e) => onFullNameChange(e.target.value)}
          autoComplete="name"
          required
        />
      </div>
      {isOrg && (
        <div className="space-y-2">
          <Label htmlFor="organizationName" className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            Organization name
          </Label>
          <Input
            id="organizationName"
            type="text"
            placeholder="Maverick Real Estate"
            value={organizationName}
            onChange={(e) => onOrganizationNameChange(e.target.value)}
            autoComplete="organization"
            readOnly={isBoundInvite}
            aria-describedby={isBoundInvite ? "organizationName-note" : undefined}
            required={!isBoundInvite}
            className={isBoundInvite ? "bg-muted text-muted-foreground" : undefined}
          />
          {isBoundInvite && (
            <p id="organizationName-note" className="text-xs text-muted-foreground">
              Set by your invitation.
            </p>
          )}
        </div>
      )}
    </>
  );
}
